import { logError } from 'fergy-core-react-logging';
import { type FunctionComponent, useState, useContext, useEffect, useRef } from 'react';
import { FEATURE_FLAGS } from '../../../constants/general';
import { SHOWROOM_LINK } from '../../../constants/links';
import {
	MATCHING_PRODUCTS_SECTION_ID,
	OVERVIEW_SECTION_ID,
	PDP_BUYSECTION_ID,
	PRODUCT_PARTS_SECTION_ID,
	PRODUCT_QA_SECTION_ID,
	PRODUCT_SPECS_SECTION_ID,
	REVIEWS_SECTION_ID
} from '../../../constants/product';
import { AddToCartContext } from '../../../contexts/add-to-cart/add-to-cart.context';
import { ProductStickyNavContext } from '../../../contexts/product-sticky-nav/product-sticky-nav.context';
import { VariantContext } from '../../../contexts/variant/variant.context';
import { AnalyticsHelper } from '../../../helpers/analytics/analytics.helper';
import { findButtonFromAnchor, getUnderlineSettings } from '../../../helpers/jump-tabs/jump-tabs.helpers';
import { buildGAButtonSelection, buildGAFindShowroomClick } from '../../../helpers/product-helper/product-analytics.helper';
import { getExtraPaddingForUnderlinePosition } from '../../../helpers/product-helper/product.helper';
import { useClientOnlyBreakpoints } from '../../../hooks/breakpoints/client-only-breakpoints.hook';
import { useFeature } from '../../../hooks/features/features.hooks';
import { type JumpTabNavButton } from '../../../types/jump-tabs.types';
import { scrollToElement } from '../../../utils/dom/dom.utils';
import { StyledButton, TextButton } from '../../buttons';
import { AlertNotification } from '../../common-components/alert-notification/alert-notification.component';
import { DropdownMenu } from '../../common-components/dropdown-menu/dropdown-menu.component';
import { Link } from '../../common-components/link/link.component';
import { ProductPrice } from '../product-price/product-price.component';
import { productStickyNavButton, removeOutline, dropDownShadow } from './product-sticky-nav.css';

export type ProductStickyNavButtonProps = {
	label: string;
	onClick: () => void;
	selected: boolean;
	id: string;
};
export const ProductStickyNavButton: FunctionComponent<ProductStickyNavButtonProps> = ({ label, onClick, selected = false, id }) => {
	const { isLarge } = useClientOnlyBreakpoints();
	const bold = selected ? 'font-medium!' : 'md:font-medium!';
	const hover = !selected && isLarge ? productStickyNavButton : '';

	return (
		<TextButton color="grey-darker" className={`f5 pa1 pa0-ns ${bold} ${hover} ${removeOutline}`} onClick={onClick} id={id}>
			{label}
		</TextButton>
	);
};

export type ProductStickyNavProps = {
	AddToCartButton: JSX.Element;
	shouldDisplayShowroomButton?: boolean;
	findShowroomIsInView?: boolean;
	removePartsSection?: boolean;
	currentActiveSection?: string;
	restrictionState?: string;
};

const INITIAL_BUTTON_WIDTH = 73;

export const ProductStickyNav: FunctionComponent<ProductStickyNavProps> = ({
	AddToCartButton,
	shouldDisplayShowroomButton,
	findShowroomIsInView,
	currentActiveSection,
	removePartsSection = false,
	restrictionState
}) => {
	// 73 is the full width of the first button's div, as it will always be labeled 'Overview'
	const initialButtonSettings = {
		width: String(INITIAL_BUTTON_WIDTH) + 'px',
		marginLeft: '0px'
	};
	const { isNotSmall, isLarge } = useClientOnlyBreakpoints();
	const [selectedTab, setSelectedTab] = useState('Overview');
	const [buttonSettings, setButtonSettings] = useState(initialButtonSettings);
	const { selection } = useContext(AddToCartContext);
	const { currentVariant } = useContext(VariantContext);
	const { shouldShowATC, setSectionToOpen } = useContext(ProductStickyNavContext);
	const luxSpecsSectionActive = useFeature(FEATURE_FLAGS.LUXURY_PDP_SPECS_SECTION, { isSiteWide: true });

	const buttonRef = useRef('');
	const navButtons: JumpTabNavButton[] = [
		{ label: 'Overview', anchor: PDP_BUYSECTION_ID, id: 'overview-button' },
		{ label: 'Details', anchor: OVERVIEW_SECTION_ID, id: 'details-button' },
		...(luxSpecsSectionActive ? [{ label: 'Specs', anchor: PRODUCT_SPECS_SECTION_ID, id: 'specs-button' }] : []),
		{
			label: 'Reviews',
			anchor: REVIEWS_SECTION_ID,
			id: 'reviews-button'
		},
		{
			label: 'Q&A',
			anchor: PRODUCT_QA_SECTION_ID,
			id: 'qa-button'
		},
		{
			label: 'Matching Products',
			anchor: MATCHING_PRODUCTS_SECTION_ID,
			id: 'matching-products-button'
		},
		{
			label: 'Parts',
			anchor: PRODUCT_PARTS_SECTION_ID,
			id: 'parts-button'
		}
	];

	if (removePartsSection) {
		navButtons.pop();
	}

	// Handler for nav bar button clicks
	const onButtonClick = (button: JumpTabNavButton) => {
		const scrollToOffset = isNotSmall ? 75 : 50;
		AnalyticsHelper.track(buildGAButtonSelection(button.label)).catch(logError);
		setButtonSettings(getUnderlineSettings(button, getExtraPaddingForUnderlinePosition(isLarge)));
		setSelectedTab(button.label);
		setSectionToOpen(button.anchor);
		scrollToElement(button.anchor, scrollToOffset);
		buttonRef.current = button.id;
	};

	const onFindShowroomClick = () => {
		AnalyticsHelper.track(buildGAFindShowroomClick()).catch(logError);
	};

	useEffect(() => {
		if (currentActiveSection) {
			const activeButton = findButtonFromAnchor(currentActiveSection, navButtons);
			// will check if user is firing a button click, if not will ignore and behave as it would on scroll
			// otherwise will check if the clicked button and the inview section are the same, if so, exit click mode
			if (buttonRef.current) {
				if (buttonRef.current === activeButton.id) {
					buttonRef.current = '';
				}
			} else {
				setButtonSettings(getUnderlineSettings(activeButton, getExtraPaddingForUnderlinePosition(isLarge)));
				setSelectedTab(activeButton.label);
			}
		}
	}, [currentActiveSection]);

	return (
		<div className="flex flex-row justify-between bg-theme-white pv2-ns ph2-m ph4-l">
			<div className="dn flex-ns flex-column pv3-ns">
				<div className="dn flex-ns flex-nowrap w-100">
					{navButtons.map((button, idx) => {
						return (
							button.label && (
								<div key={idx} data-testid="pdp-nav-button" className="pr5-l pr4-m">
									<ProductStickyNavButton
										label={button.label}
										selected={selectedTab === button.label ? true : false}
										onClick={() => onButtonClick(button)}
										id={button.id}
									/>
								</div>
							)
						);
					})}
				</div>
				{/* This div's purpose is to house the sliding underline bar.
				The transition setting provides for a more visually pleasing experience */}
				<div className="w-100">
					<div
						data-testid="underline"
						className={`bb-ns bw1-ns`}
						style={{
							// width and margin-left (relative to the target button) are both available from the button dom element,
							// and are used here after calculation to make the sliding bar have the proper width and positioning.
							width: buttonSettings.width,
							marginLeft: buttonSettings.marginLeft,
							transition: 'all 0.5s'
						}}
					/>
				</div>
			</div>

			<div className="flex items-center mh2 w-100 dn-ns ph2 h3">
				<DropdownMenu
					title={selectedTab}
					shouldCloseOnChoice={true}
					containerClassName="bg-theme-white theme-black pointer relative w-100"
					itemClassName="bt-0"
					menuSelectClassName="pv1 flex justify-between bb b--theme-black"
					dropdownClassName={`bw0 pl3 pv2 ${dropDownShadow}`}>
					{navButtons.map((button, idx) => {
						return (
							button.label && (
								<ProductStickyNavButton
									key={idx}
									label={button.label}
									selected={selectedTab === button.label ? true : false}
									onClick={() => onButtonClick(button)}
									id={button.id}
								/>
							)
						);
					})}
				</DropdownMenu>
			</div>
			{(shouldShowATC || !findShowroomIsInView) && (
				<div className={`flex items-center`}>
					{!shouldDisplayShowroomButton && (
						<div className="dn db-ns font-medium f3 pr3">
							<ProductPrice
								{...currentVariant.priceInfo}
								pricingDisplay={currentVariant.productPricingDisplay}
								variantId={currentVariant.id}
								current={selection?.total || currentVariant.priceInfo.current}
								displayTooltip={false}
								isStickyNav={true}
							/>
						</div>
					)}
					{shouldDisplayShowroomButton ? (
						<Link url={SHOWROOM_LINK}>
							<StyledButton onClick={onFindShowroomClick} buttonStyle="PRIMARY">
								Find a Showroom
							</StyledButton>
						</Link>
					) : !restrictionState ? (
						AddToCartButton
					) : (
						<AlertNotification alertType="warning" size="SMALL" showCloseButton={false}>
							{`Unavailable in ${restrictionState}`}
						</AlertNotification>
					)}
				</div>
			)}
		</div>
	);
};
